// Public /pay/:invoiceId — no auth. The client sees the invoice summary and
// clicks "Pay online" to be redirected to Stripe Checkout.
import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND}/api/public` });

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function PayInvoice() {
  const { invoiceId } = useParams();
  const [search] = useSearchParams();
  const cancelled = search.get("cancelled") === "1";
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invoices/${invoiceId}`);
        setInv(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Invoice not found.");
      } finally { setLoading(false); }
    })();
  }, [invoiceId]);

  const startCheckout = async () => {
    setStarting(true); setErr("");
    try {
      const { data } = await api.post("/payments/checkout", {
        invoice_id: invoiceId,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start checkout. Please try again.");
      setStarting(false);
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 text-neutral-900 py-10 px-4" data-testid="pay-invoice-page">
      <div className="max-w-lg mx-auto bg-white border border-neutral-200 rounded-xl overflow-hidden shadow-sm">
        <div className="px-6 py-5 border-b border-neutral-200">
          <div className="text-xs uppercase tracking-wider text-neutral-500">Invoice</div>
          <div className="text-2xl font-semibold mt-1" data-testid="pay-invoice-number">{inv?.invoice_number || "—"}</div>
          {inv?.customer_name && <div className="text-sm text-neutral-600 mt-1">Billed to <b>{inv.customer_name}</b></div>}
        </div>

        {loading && <div className="p-6 text-sm text-neutral-500">Loading invoice…</div>}
        {err && !inv && <div className="p-6 text-sm text-red-600">{err}</div>}

        {inv && (
          <>
            <div className="px-6 py-4 divide-y divide-neutral-100 text-sm">
              {(inv.items || []).map((li, i) => (
                <div key={i} className="flex items-center justify-between py-2">
                  <div className="text-neutral-700">{li.description || "Item"} <span className="text-neutral-400">× {li.quantity}</span></div>
                  <div className="text-neutral-900 tabular-nums">{money(li.amount)}</div>
                </div>
              ))}
            </div>
            <div className="px-6 py-4 border-t border-neutral-200 space-y-1.5 text-sm">
              <div className="flex justify-between text-neutral-600"><span>Subtotal</span><span className="tabular-nums">{money(inv.subtotal)}</span></div>
              {inv.tax > 0 && <div className="flex justify-between text-neutral-600"><span>Tax</span><span className="tabular-nums">{money(inv.tax)}</span></div>}
              {inv.amount_paid > 0 && <div className="flex justify-between text-emerald-700"><span>Already paid</span><span className="tabular-nums">− {money(inv.amount_paid)}</span></div>}
              <div className="flex justify-between text-lg font-semibold pt-2 border-t border-neutral-200 mt-2">
                <span>Balance due</span>
                <span className="tabular-nums" data-testid="pay-invoice-balance">{money(inv.balance)}</span>
              </div>
            </div>

            <div className="px-6 py-5 border-t border-neutral-200 space-y-3">
              {cancelled && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5" data-testid="pay-cancelled-banner">
                  Payment was cancelled. You can try again below.
                </div>
              )}
              {inv.balance <= 0 ? (
                <div className="text-center text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3 font-medium" data-testid="pay-already-paid">
                  ✓ This invoice is fully paid. Thank you!
                </div>
              ) : (
                <button
                  onClick={startCheckout}
                  disabled={starting}
                  className="w-full h-12 rounded-md bg-neutral-900 text-white font-semibold hover:bg-neutral-800 disabled:bg-neutral-400 transition-colors"
                  data-testid="pay-invoice-btn"
                >
                  {starting ? "Redirecting to Stripe…" : `Pay ${money(inv.balance)} securely`}
                </button>
              )}
              {err && inv && <div className="text-xs text-red-600" data-testid="pay-invoice-error">{err}</div>}
              <div className="text-center">
                <a
                  href={`/invoice/${invoiceId}/view`}
                  className="text-xs text-neutral-500 underline underline-offset-2 hover:text-neutral-800"
                  data-testid="pay-invoice-print-link"
                >
                  Print or save a copy
                </a>
              </div>
              <div className="flex items-center justify-center gap-1.5 text-[11px] text-neutral-500 pt-1">
                <span>Powered by</span>
                <span className="font-semibold text-neutral-700">Stripe</span>
                <span>· 256-bit SSL</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
